import { DeleteOutlined, EditOutlined, Visibility } from '@mui/icons-material';
import {
    Chip,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import ColumnHeaderFilter from './columnHeaderFilter';
import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';

import { ACTIONS } from 'constant';
import useTranslate from 'hooks/useTranslate';
import type { DocumentType } from 'types';
import type { DocumentTemplateRecord, ServiceCountryCode } from 'types/servicesDocuments';
import {
    canEditServicesDocumentRecord,
    isUniversalRecord,
    localizedTemplateName,
} from 'utils/servicesDocuments';

export type DocumentTemplateRowAction =
    | typeof ACTIONS.EDIT
    | typeof ACTIONS.DELETE
    | typeof ACTIONS.PREVIEW_FILE;

interface Props {
    loading: boolean;
    templates: DocumentTemplateRecord[];
    userRole: string;
    currentProviderId: number | null;
    language?: string;
    countryCode: ServiceCountryCode | '';
    onCountryChange: (value: ServiceCountryCode | '') => void;
    documentType: DocumentType | '';
    onDocumentTypeChange: (value: DocumentType | '') => void;
    handleAction: (action: DocumentTemplateRowAction, template: DocumentTemplateRecord) => void;
}

const TEMPLATE_COLUMN_COUNT = 5;

const DocumentTemplatesList = ({
    loading,
    templates,
    userRole,
    currentProviderId,
    language,
    countryCode,
    onCountryChange,
    documentType,
    onDocumentTypeChange,
    handleAction,
}: Props) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('md'));

    const countryFilter = (
        <ColumnHeaderFilter
            label={translate('servicesDocuments.columns.country')}
            value={countryCode}
            idleValue=""
            onChange={(value) => onCountryChange(value as ServiceCountryCode | '')}
            options={[
                { value: '', label: translate('servicesDocuments.filters.allCountries') },
                { value: 'CA', label: 'CA' },
                { value: 'AU', label: 'AU' },
                { value: 'UK', label: 'UK' },
            ]}
        />
    );
    const documentTypeFilter = (
        <ColumnHeaderFilter
            label={translate('servicesDocuments.columns.type')}
            value={documentType}
            idleValue=""
            onChange={(value) => onDocumentTypeChange(value as DocumentType | '')}
            options={[
                { value: '', label: translate('servicesDocuments.filters.allTypes') },
                { value: 'ORGANIZATION', label: 'ORGANIZATION' },
                { value: 'USER', label: 'USER' },
                { value: 'SERVICES_ALL', label: 'SERVICES_ALL' },
                { value: 'SERVICES_SELECTED', label: 'SERVICES_SELECTED' },
            ]}
        />
    );
    const emptyResult = (
        <Result
            title={translate('servicesDocuments.templates.emptyTitle')}
            description={translate('servicesDocuments.templates.emptyDescription')}
            sx={{ border: 'none', borderRadius: 0, background: 'transparent' }}
        />
    );

    if (mobile) {
        return (
            <Stack spacing={1.5} data-testid="document-templates-list-mobile">
                <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {documentTypeFilter}
                    {countryFilter}
                </Stack>
                {loading ? <TableSkeleton tableColumns={TEMPLATE_COLUMN_COUNT} /> : null}
                {!loading && !templates.length ? emptyResult : null}
                {!loading
                    ? templates.map((template) => {
                          const editable = canEditServicesDocumentRecord(
                              userRole,
                              template.service_provider_id,
                              currentProviderId
                          );
                          const name = localizedTemplateName(template, language);
                          return (
                              <Paper
                                  key={template.id}
                                  variant="outlined"
                                  sx={{ p: 1.5, borderRadius: 2 }}
                              >
                                  <Stack spacing={0.75}>
                                      <Typography fontWeight={700}>{name}</Typography>
                                      <Typography variant="body2" color="text.secondary">
                                          {template.document_type}
                                          {template.country_code
                                              ? ` · ${template.country_code}`
                                              : ''}
                                      </Typography>
                                      {isUniversalRecord(template.service_provider_id) ? (
                                          <Chip
                                              size="small"
                                              label={translate('servicesDocuments.scope.universal')}
                                          />
                                      ) : null}
                                      <Stack
                                          direction="row"
                                          spacing={0.5}
                                          justifyContent="flex-end"
                                      >
                                          <Tooltip title={translate('preview')}>
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      aria-label={translate('preview')}
                                                      disabled={!template.template_uri}
                                                      onClick={() =>
                                                          handleAction(
                                                              ACTIONS.PREVIEW_FILE,
                                                              template
                                                          )
                                                      }
                                                  >
                                                      <Visibility fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>
                                          <Tooltip title={translate('edit')}>
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      aria-label={translate('edit')}
                                                      disabled={!editable}
                                                      onClick={() =>
                                                          handleAction(ACTIONS.EDIT, template)
                                                      }
                                                  >
                                                      <EditOutlined fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>
                                          <Tooltip title={translate('delete')}>
                                              <span>
                                                  <IconButton
                                                      size="small"
                                                      aria-label={translate('delete')}
                                                      disabled={!editable}
                                                      onClick={() =>
                                                          handleAction(ACTIONS.DELETE, template)
                                                      }
                                                  >
                                                      <DeleteOutlined fontSize="small" />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>
                                      </Stack>
                                  </Stack>
                              </Paper>
                          );
                      })
                    : null}
            </Stack>
        );
    }

    return (
        <TableContainer component={Paper} variant="outlined" data-testid="document-templates-table">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{translate('name')}</TableCell>
                        <TableCell>{documentTypeFilter}</TableCell>
                        <TableCell>{countryFilter}</TableCell>
                        <TableCell>{translate('servicesDocuments.columns.scope')}</TableCell>
                        <TableCell align="right">{translate('actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading
                        ? Array.from({ length: 6 }).map((_, rowIndex) => (
                              <TableRow key={`templates-skeleton-${rowIndex}`}>
                                  {Array.from({ length: TEMPLATE_COLUMN_COUNT }).map(
                                      (__, colIndex) => (
                                          <TableCell key={colIndex}>
                                              <Skeleton variant="rounded" height={24} width="80%" />
                                          </TableCell>
                                      )
                                  )}
                              </TableRow>
                          ))
                        : null}
                    {!loading && !templates.length ? (
                        <TableRow>
                            <TableCell
                                colSpan={TEMPLATE_COLUMN_COUNT}
                                sx={{ p: 0, borderBottom: 'none' }}
                            >
                                {emptyResult}
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {!loading
                        ? templates.map((template) => {
                              const editable = canEditServicesDocumentRecord(
                                  userRole,
                                  template.service_provider_id,
                                  currentProviderId
                              );
                              const name = localizedTemplateName(template, language);
                              return (
                                  <TableRow key={template.id} hover>
                                      <TableCell>{name}</TableCell>
                                      <TableCell>{template.document_type || '—'}</TableCell>
                                      <TableCell>{template.country_code || '—'}</TableCell>
                                      <TableCell>
                                          {isUniversalRecord(template.service_provider_id)
                                              ? translate('servicesDocuments.scope.universal')
                                              : translate('servicesDocuments.scope.provider')}
                                      </TableCell>
                                      <TableCell align="right">
                                          <Stack
                                              direction="row"
                                              spacing={0.5}
                                              justifyContent="flex-end"
                                          >
                                              <Tooltip title={translate('preview')}>
                                                  <span>
                                                      <IconButton
                                                          size="small"
                                                          aria-label={translate('preview')}
                                                          disabled={!template.template_uri}
                                                          onClick={() =>
                                                              handleAction(
                                                                  ACTIONS.PREVIEW_FILE,
                                                                  template
                                                              )
                                                          }
                                                      >
                                                          <Visibility fontSize="small" />
                                                      </IconButton>
                                                  </span>
                                              </Tooltip>
                                              <Tooltip title={translate('edit')}>
                                                  <span>
                                                      <IconButton
                                                          size="small"
                                                          aria-label={translate('edit')}
                                                          disabled={!editable}
                                                          onClick={() =>
                                                              handleAction(ACTIONS.EDIT, template)
                                                          }
                                                      >
                                                          <EditOutlined fontSize="small" />
                                                      </IconButton>
                                                  </span>
                                              </Tooltip>
                                              <Tooltip title={translate('delete')}>
                                                  <span>
                                                      <IconButton
                                                          size="small"
                                                          aria-label={translate('delete')}
                                                          disabled={!editable}
                                                          onClick={() =>
                                                              handleAction(ACTIONS.DELETE, template)
                                                          }
                                                      >
                                                          <DeleteOutlined fontSize="small" />
                                                      </IconButton>
                                                  </span>
                                              </Tooltip>
                                          </Stack>
                                      </TableCell>
                                  </TableRow>
                              );
                          })
                        : null}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default DocumentTemplatesList;
